// The station popup's counts-over-time series: a pure transform from a
// station's Trap Instances to one line per species over the collection dates.
// It lives here, not in the chart component, so the aggregation can be read
// and tested without mounting a canvas.

import { localDayString } from "./columns";
import { formatDateOnly } from "@/format";
import type { MapReviewTrapInstanceRow } from "@/type/api";

/** One species' catch over the station's collection dates. */
export interface StationCountsDataset {
	label: string;
	data: number[];
}

/**
 * The station's trapping series as the chart reads it: the collection dates on
 * the x-axis, one line per species. `labels` and every dataset's `data` share
 * their order, so `data[i]` is the species' catch on `labels[i]`.
 */
export interface StationCountsSeries {
	labels: string[];
	datasets: StationCountsDataset[];
}

/** One colour per species line, cycled when a station's catch runs past it. */
export const SPECIES_PALETTE = [
	"#0d6efd",
	"#dc3545",
	"#198754",
	"#fd7e14",
	"#6f42c1",
	"#20c997",
	"#d63384",
	"#6c757d",
] as const;

/**
 * Aggregate the station's instances into one line per species over the
 * collection dates. Only Analyzed samples with a collection instant are
 * plotted: a Set instance has no sample and an unanalyzed one has no
 * composition, so neither places a catch on a day. A species' value on a day
 * is the sum of its matrix entries that day — both sexes together, which is
 * what "one line per species" means.
 */
export function buildStationCountsSeries(
	rows: MapReviewTrapInstanceRow[],
): StationCountsSeries {
	const byDay = new Map<string, Map<string, number>>();
	for (const row of rows) {
		if (row.counts.state !== "analyzed" || row.collected_at == null) continue;
		const day = localDayString(new Date(row.collected_at));
		let tally = byDay.get(day);
		if (!tally) {
			tally = new Map();
			byDay.set(day, tally);
		}
		for (const entry of row.counts.matrix) {
			tally.set(entry.species, (tally.get(entry.species) ?? 0) + entry.count);
		}
	}
	const days = [...byDay.keys()].sort();
	const species = new Set<string>();
	for (const tally of byDay.values()) {
		for (const name of tally.keys()) species.add(name);
	}
	return {
		// The day is a local calendar day ("YYYY-MM-DD"); it is read back at
		// local midnight so the label names the day the samples were collected
		// on, not the UTC day a bare date string would parse as.
		labels: days.map((day) => formatDateOnly(new Date(`${day}T00:00:00`))),
		datasets: [...species].sort().map((name) => ({
			label: name,
			data: days.map((day) => byDay.get(day)?.get(name) ?? 0),
		})),
	};
}
